/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '../../api';
import type { Order, OrderDetail, OrderStatus } from '../../api/areas/commerce';
import { DataTable, FilterBar, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { BulkList, useSelection } from '../../components/BulkList';
import { CountTiles } from '../../components/CountTiles';
import { SavedViews } from '../../components/SavedViews';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { ProductItem, ProductOperation } from './ProductItem';
import { OrderQrAction } from './PaymentQr';
import { CustomerListFilter } from './CustomerListFilter';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { SystemReason } from '../../components/SystemReason';
import { formatCount, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import {
  Amount,
  cellLink,
  clearParams,
  CustomerRef,
  DOC_TYPE,
  FilterSelect,
  HOLD_STATUS,
  holdPath,
  InvoiceRef,
  JournalTable,
  mutedSmall,
  ORDER_ITEM_STATUS,
  ORDER_STATUS,
  OrderRef,
  Pager,
  PAYMENT_STATUS,
  PaymentRef,
  quotePath,
  RefreshButton,
  REFUND_STATUS,
  RefundRef,
  ResetFilters,
  RichText,
  setParam,
  Status,
  useEnumLabel,
  useKeyset,
  When,
} from './shared';

const FILTER_KEYS = ['status', 'customer_id'] as const;

/** `/orders`: customer orders, newest first; status tiles with exact counts, saved views, selection. */
export function OrdersPage() {
  const { t } = useTranslation('commerce');
  const { t: tu } = useTranslation('ux');
  const label = useEnumLabel('commerce');
  useDocumentTitle(t('orders.title'));
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? '') as OrderStatus | '';
  const customer = params.get('customer_id') ?? '';
  const pager = useKeyset(params.toString());
  const result = useApi(
    (signal) => api.listOrders({ status: status || undefined, customer_id: customer || undefined, limit: 50, before: pager.before }, { signal }),
    [status, customer, pager.before],
  );
  const rows = result.status === 'success' ? result.data.items : [];
  const counts = useApi((signal) => api.getCounts({ signal }), []);
  // Orders have no staff action in the system: the selection is exported.
  const selection = useSelection<Order>((o) => o.id);
  const orderName = (o: Order) => [o.domains[0] ?? o.id, o.item_count > 1 ? `+${o.item_count - 1}` : ''].filter(Boolean).join(' ');

  const columns: Column<Order>[] = [
    selection.column(rows, (o) => tu('bulkList.select', { name: orderName(o) }), tu('bulkList.selectAll')),
    { key: 'order', header: t('orders.col.order'), cell: (o) => <OrderRef id={o.id} domain={o.domains[0]} count={o.item_count} at={o.created_at} /> },
    { key: 'customer', header: t('orders.col.customer'), cell: (o) => <CustomerRef id={o.customer_id} name={o.customer_name} /> },
    { key: 'status', header: t('orders.col.status'), cell: (o) => <Status group="orderStatus" tones={ORDER_STATUS} value={o.status} /> },
    { key: 'items', header: t('orders.col.items'), numeric: true, cell: (o) => formatCount(o.item_count) },
    { key: 'total', header: t('orders.col.total'), numeric: true, cell: (o) => <Amount amount={o.total} currency={o.currency} className="font-semibold" /> },
    { key: 'placed', header: t('orders.col.placed'), cell: (o) => <When at={o.created_at} /> },
    { key: 'updated', header: t('orders.col.updated'), cell: (o) => <When at={o.updated_at} /> },
  ];

  return (
    <>
      <PageHeader title={t('orders.title')} crumbs={[{ label: t('crumb') }]} subtitle={<RichText k="orders.subtitle" />} actions={<RefreshButton onClick={result.reload} />} />
      <CountTiles
        label={t('common.status')}
        allLabel={tu('lists.all')}
        options={Object.keys(ORDER_STATUS).map((s) => ({ value: s, label: label('orderStatus', s), tone: ORDER_STATUS[s] }))}
        counts={counts.status === 'loading' ? null : counts.status === 'success' ? (counts.data.orders?.by_status ?? undefined) : undefined}
        current={status}
        onPick={(v) => setParams(setParam(params, 'status', v))}
      />
      <FilterBar actions={<ResetFilters show={Boolean(status || customer)} onReset={() => setParams(clearParams(params, FILTER_KEYS))} />}>
        <FilterSelect label={t('common.status')} value={status} options={Object.keys(ORDER_STATUS)} group="orderStatus" className="sm:w-52" onChange={(v) => setParams(setParam(params, 'status', v))} />
        <CustomerListFilter value={customer} name={rows[0]?.customer_name} onChange={(v) => setParams(setParam(params, 'customer_id', v))} />
      </FilterBar>
      <SavedViews list="orders" keys={FILTER_KEYS} />
      <BulkList
        selection={selection}
        actions={[]}
        rowKey={(o) => o.id}
        rowLabel={orderName}
        onDone={result.reload}
        csv={{
          name: 'orders',
          columns: [
            { header: t('orders.col.order'), value: (o) => o.id },
            { header: tu('lists.csv.domains'), value: (o) => o.domains.join(' ') },
            { header: t('orders.col.customer'), value: (o) => o.customer_name },
            { header: t('orders.col.status'), value: (o) => label('orderStatus', o.status) },
            { header: t('orders.col.items'), value: (o) => o.item_count },
            { header: t('orders.col.total'), value: (o) => o.total },
            { header: tu('lists.csv.currency'), value: (o) => o.currency },
            { header: t('orders.col.placed'), value: (o) => o.created_at },
            { header: t('orders.col.updated'), value: (o) => o.updated_at },
          ],
        }}
      />
      <Section flush>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={5} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} title={t('orders.loadError')} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <>
            <DataTable columns={columns} rows={rows} rowKey={(o) => o.id} empty={<EmptyState title={t('orders.empty')} />} />
            <Pager cursors={pager.cursors} next={result.data.next_before} onNewer={pager.newer} onOlder={pager.older} />
          </>
        ) : null}
      </Section>
    </>
  );
}

type Item = OrderDetail['items'][number];

/** `/orders/:orderId`: the names ordered with their funds, plus payments, invoices, refunds and ledger entries. */
export function OrderDetailPage() {
  const { t } = useTranslation('commerce');
  const label = useEnumLabel('commerce');
  const { orderId = '' } = useParams();
  useDocumentTitle(t('order.title'));
  const result = useApi((signal) => api.getOrder(orderId, { signal }), [orderId]);
  const crumbs = [{ label: t('crumb') }, { label: t('orders.title'), to: '/orders' }];
  if (result.status === 'loading') return <Loading rows={4} />;
  if (result.status === 'error') {
    return (
      <>
        <PageHeader title={t('order.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={result.reload} title={t('order.loadError')} />
      </>
    );
  }
  const { order: o, items, payments, invoices, refunds, journal_entries } = result.data;
  const first = o.domains[0];
  const docCount = payments.length + invoices.length + refunds.length;
  const fulfilled = items.filter((i) => i.status === 'FULFILLED').length;
  const statusTone = ORDER_STATUS[o.status] ?? 'neutral';

  const itemColumns: Column<Item>[] = [
    { key: 'domain', header: t('order.itemCol.domain'), cell: (i) => <ProductItem item={i} /> },
    {
      key: 'operation',
      header: t('order.itemCol.operation'),
      className: 'whitespace-nowrap',
      cell: (i) => (
        <>
          <ProductOperation item={i} />
        </>
      ),
    },
    { key: 'status', header: t('order.itemCol.status'), cell: (i) => <Status group="orderItemStatus" tones={ORDER_ITEM_STATUS} value={i.status} /> },
    { key: 'amount', header: t('order.itemCol.amount'), numeric: true, cell: (i) => <Amount amount={i.amount} currency={i.currency} /> },
    {
      key: 'hold',
      header: t('order.itemCol.hold'),
      cell: (i) =>
        i.hold_id && i.hold_status ? (
          <Link to={holdPath(i.hold_id)} className="hover:underline">
            <Status group="holdStatus" tones={HOLD_STATUS} value={i.hold_status} />
          </Link>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'quote',
      header: t('order.itemCol.quote'),
      cell: (i) => (
        <Link to={quotePath(i.quote_id)} className={cellLink}>
          {t('order.viewQuote')}
        </Link>
      ),
    },
    {
      key: 'workflow',
      header: t('order.itemCol.workflow'),
      cell: (i) =>
        i.workflow_id ? (
          <Link to={`/ops/workflows/${encodeURIComponent(i.workflow_id)}`} className={cellLink}>
            {t('order.viewWorkflow')}
          </Link>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title={first ? t('order.titleFor', { domain: first }) : t('order.title')}
        crumbs={[...crumbs, { label: first ?? t('order.title') }]}
        subtitle={o.domains.length > 1 ? o.domains.join(' · ') : undefined}
        actions={
          <>
            {o.status === 'PLACED' ? <OrderQrAction orderId={o.id} onChanged={result.reload} /> : null}
            <RefreshButton onClick={result.reload} />
          </>
        }
      />
      <div className="flex flex-col gap-6">
        <StatGrid className="xl:grid-cols-3">
          <StatCard label={t('order.stat.status')} value={label('orderStatus', o.status)} tone={statusTone} />
          <StatCard label={t('order.stat.total')} value={<Amount amount={o.total} currency={o.currency} />} />
          <StatCard label={t('order.stat.items')} value={formatCount(items.length)} hint={t('order.stat.fulfilled', { count: fulfilled })} />
        </StatGrid>

        <Section>
          <KeyValue
            columns={3}
            rows={[
              [t('common.status'), <Status key="s" group="orderStatus" tones={ORDER_STATUS} value={o.status} />],
              [t('common.total'), <Amount key="t" amount={o.total} currency={o.currency} className="font-semibold" />],
              [t('common.customer'), <CustomerRef key="c" id={o.customer_id} name={o.customer_name} />],
              [t('order.field.placed'), formatDateTime(o.created_at)],
              [t('common.updated'), formatDateTime(o.updated_at)],
              [t('order.field.id'), <CopyText key="id" value={o.id} />],
            ]}
          />
        </Section>

        <Tabs defaultValue="items">
          <TabsList className="max-w-full justify-start overflow-x-auto">
            <TabsTrigger value="items">{t('order.tab.items', { count: items.length })}</TabsTrigger>
            <TabsTrigger value="ledger">{t('order.tab.ledger', { count: journal_entries.length })}</TabsTrigger>
            <TabsTrigger value="documents">{t('order.tab.documents', { count: docCount })}</TabsTrigger>
          </TabsList>
          <TabsContent value="items" className="space-y-3">
            <Section flush>
              <DataTable columns={itemColumns} rows={items} rowKey={(i) => i.id} />
            </Section>
          </TabsContent>
          <TabsContent value="ledger">
            <Section flush>
              <JournalTable entries={journal_entries} empty={<EmptyState title={t('order.ledgerEmpty')} />} />
            </Section>
          </TabsContent>
          <TabsContent value="documents">
            <Section>
              {docCount === 0 ? <p className="text-sm text-muted-foreground">{t('order.noDocuments')}</p> : null}
              <div className="space-y-5">
                {payments.length ? (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold">{t('order.payments')}</h3>
                    <ul className="space-y-1.5 text-sm">
                      {payments.map((p) => (
                        <li key={p.id} className="flex flex-wrap items-center gap-2">
                          <PaymentRef payment={p} />
                          <Amount amount={p.amount} currency={p.currency} />
                          <Status group="paymentStatus" tones={PAYMENT_STATUS} value={p.status} />
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {invoices.length ? (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold">{t('order.invoices')}</h3>
                    <ul className="space-y-1.5 text-sm">
                      {invoices.map((inv) => (
                        <li key={inv.id} className="flex flex-wrap items-center gap-2">
                          <Status group="docType" tones={DOC_TYPE} value={inv.document_type} />
                          <InvoiceRef invoice={inv} />
                          <Amount amount={inv.total} currency={inv.currency} />
                          <span className={mutedSmall}>{t('order.issuedAt', { at: formatDateTime(inv.issued_at) })}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {refunds.length ? (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold">{t('order.refunds')}</h3>
                    <ul className="space-y-1.5 text-sm">
                      {refunds.map((r) => (
                        <li key={r.id} className="flex flex-wrap items-center gap-2">
                          <RefundRef id={r.id} domain={r.domain_name} />
                          <Amount amount={r.amount} currency={r.currency} />
                          <Status group="refundStatus" tones={REFUND_STATUS} value={r.status} />
                          <span className={mutedSmall}>
                            <SystemReason reason={r.reason} />
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </Section>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
